import { yupResolver } from "@hookform/resolvers/yup";
import React, { useRef, useState } from "react";
import {
  Control,
  Controller,
  FieldPath,
  useForm,
} from "react-hook-form";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import FormContainer from "../../Components/Form/FormContainer";
import logo from "../../Components/Navbar/assets/logo.svg";
import { Button } from "../../Components/ui/buttons/button";
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "../../Components/ui/shadcn/forms/field";
import { Input } from "../../Components/ui/input";
import { Card, CardContent } from "../../Components/ui/shadcn/card";
import { LOGIN_ROUTE } from "../../global/constants/routes/defaultConstants";
import {
  RegistrationRequestInput,
  RegistrationRequestServiceError,
} from "../../global/types/registrationRequest";
import { createRegistrationRequest } from "../../services/registrationRequest";
import { formatCnpj, isValidCnpj, normalizeCnpj } from "../../utils/cnpj";
import { BrandPanel, Content, FormArea, Logo, PageContainer } from "./styles";

const registrationRequestSchema: yup.ObjectSchema<RegistrationRequestInput> =
  yup.object({
    fullName: yup
      .string()
      .trim()
      .required("Informe seu nome completo")
      .max(255, "O nome completo deve ter no máximo 255 caracteres"),
    email: yup
      .string()
      .trim()
      .lowercase()
      .required("Informe seu e-mail")
      .email("Informe um e-mail válido")
      .max(320, "O e-mail deve ter no máximo 320 caracteres"),
    institutionName: yup
      .string()
      .trim()
      .required("Informe o nome da instituição")
      .max(255, "O nome da instituição deve ter no máximo 255 caracteres"),
    institutionCnpj: yup
      .string()
      .required("Informe o CNPJ da instituição")
      .test("valid-cnpj", "Informe um CNPJ válido", isValidCnpj),
    institutionCity: yup
      .string()
      .trim()
      .required("Informe a cidade da instituição")
      .max(120, "A cidade deve ter no máximo 120 caracteres"),
  });

const API_FIELD_MAP: Record<string, FieldPath<RegistrationRequestInput>> = {
  full_name: "fullName",
  email: "email",
  institution_name: "institutionName",
  institution_cnpj: "institutionCnpj",
  institution_city: "institutionCity",
};

interface TextInputFieldProps {
  control: Control<RegistrationRequestInput>;
  name: FieldPath<RegistrationRequestInput>;
  label: string;
  placeholder: string;
  type?: React.HTMLInputTypeAttribute;
  maxLength?: number;
  formatValue?: (value: string) => string;
}

const TextInputField = ({
  control,
  name,
  label,
  placeholder,
  type = "text",
  maxLength,
  formatValue,
}: TextInputFieldProps) => {
  const inputId = `registration-request-${name}`;
  const errorId = `${inputId}-error`;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <Field data-invalid={fieldState.invalid}>
          <FieldLabel htmlFor={inputId}>{label}</FieldLabel>
          <Input
            {...field}
            id={inputId}
            type={type}
            placeholder={placeholder}
            maxLength={maxLength}
            aria-required="true"
            autoComplete="off"
            aria-invalid={fieldState.invalid}
            aria-describedby={fieldState.invalid ? errorId : undefined}
            onChange={(event) => {
              const value = formatValue
                ? formatValue(event.target.value)
                : event.target.value;
              field.onChange(value);
            }}
          />
          {fieldState.invalid && (
            <FieldError id={errorId} errors={[fieldState.error]} />
          )}
        </Field>
      )}
    />
  );
};

const defaultValues: RegistrationRequestInput = {
  fullName: "",
  email: "",
  institutionName: "",
  institutionCnpj: "",
  institutionCity: "",
};

export const RegistrationRequest: React.FC = () => {
  const navigate = useNavigate();
  const submissionInFlight = useRef(false);
  const [wasCreated, setWasCreated] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const form = useForm<RegistrationRequestInput>({
    resolver: yupResolver(registrationRequestSchema),
    defaultValues,
  });

  const handleSubmit = async (values: RegistrationRequestInput) => {
    if (submissionInFlight.current) return;

    submissionInFlight.current = true;
    setSubmissionError(null);
    try {
      await createRegistrationRequest({
        ...values,
        institutionCnpj: normalizeCnpj(values.institutionCnpj),
      });
      setWasCreated(true);
    } catch (error) {
      if (error instanceof RegistrationRequestServiceError) {
        setSubmissionError(error.message);
        if (error.code === "VALIDATION") {
          error.validationDetails.forEach((detail) => {
            const apiField = detail.loc[detail.loc.length - 1];
            const field =
              typeof apiField === "string" ? API_FIELD_MAP[apiField] : undefined;
            if (field) {
              form.setError(field, {
                type: "server",
                message: "Revise este campo e tente novamente",
              });
            }
          });
        }
      } else {
        setSubmissionError(
          "Não foi possível enviar a solicitação. Tente novamente."
        );
      }
    } finally {
      submissionInFlight.current = false;
    }
  };

  return (
    <PageContainer>
      <BrandPanel aria-hidden="true" />
      <Content>
        <FormArea>
          <Logo src={logo} alt="Atlas" />
          {wasCreated ? (
            <Card className="mx-auto mt-10 w-full max-w-md px-2 py-4">
              <CardContent className="flex flex-col gap-4 pt-3 text-center">
                <h1 className="text-xl font-semibold text-[#0c2856]">
                  Solicitação recebida
                </h1>
                <p role="status" className="text-sm leading-6 text-gray-700">
                  Sua solicitação está aguardando análise. O envio não cria uma
                  conta imediatamente; você receberá as próximas orientações
                  após a revisão.
                </p>
                <Button
                  type="button"
                  className="w-full bg-[#0c2856] text-white hover:bg-[#091831ff]"
                  onClick={() => navigate(LOGIN_ROUTE)}
                >
                  Voltar ao login
                </Button>
              </CardContent>
            </Card>
          ) : (
            <FormContainer
              methods={form}
              onSubmit={handleSubmit}
              submitLabel="Enviar solicitação"
              submittingLabel="Enviando solicitação..."
              disabled={form.formState.isSubmitting}
            >
              <div className="w-full">
                <h1 className="text-xl font-semibold text-[#0c2856]">
                  Solicitar cadastro
                </h1>
                <p className="mt-2 text-sm leading-5 text-gray-600">
                  Preencha os dados abaixo. Sua solicitação será analisada antes
                  da criação da conta.
                </p>
              </div>
              {submissionError && (
                <div
                  role="alert"
                  className="w-full rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800"
                >
                  {submissionError}
                </div>
              )}
              <FieldGroup>
                <TextInputField
                  control={form.control}
                  name="fullName"
                  label="Nome completo"
                  placeholder="Digite seu nome completo"
                  maxLength={255}
                />
                <TextInputField
                  control={form.control}
                  name="email"
                  label="E-mail"
                  placeholder="email@instituicao.com"
                  type="email"
                  maxLength={320}
                />
                <TextInputField
                  control={form.control}
                  name="institutionName"
                  label="Instituição"
                  placeholder="Nome da instituição"
                  maxLength={255}
                />
                <TextInputField
                  control={form.control}
                  name="institutionCnpj"
                  label="CNPJ da instituição"
                  placeholder="00.000.000/0000-00"
                  maxLength={18}
                  formatValue={formatCnpj}
                />
                <TextInputField
                  control={form.control}
                  name="institutionCity"
                  label="Cidade da instituição"
                  placeholder="Cidade"
                  maxLength={120}
                />
              </FieldGroup>
            </FormContainer>
          )}
        </FormArea>
      </Content>
    </PageContainer>
  );
};
